import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  SimpleGrid,
  Button,
  Box,
  Skeleton,
  ThemeIcon,
} from '@mantine/core';
import {
  IconSparkles,
  IconFileText,
  IconArrowRight,
  IconStar,
  IconClock,
  IconBuildingChurch,
  IconLock,
  IconBook,
  IconPlayerPlay,
  IconQuote,
} from '@tabler/icons-react';
import { usePublicCoursesService } from '@actschurch/ccms-sdk';
import { AISearchBar } from '@/components/AISearchBar';
import { CourseSection } from '@/components/CourseSection';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { ministryHref } from '@/utils/slug';

const FEATURED_LIMIT = 8;
const LATEST_LIMIT = 8;
const MINISTRY_LIMIT = 8;

/** Best-available "added on" timestamp for newest-first sorting. */
function publishedAt(c: { publishedDateTime?: number | null; createdDateTime?: number | null }): number {
  return c.publishedDateTime ?? c.createdDateTime ?? 0;
}

/**
 * Resource Centre landing page.
 *
 * Sections: hero → Featured courses → Latest courses → Browse by ministry →
 * (anonymous) sign-in prompt → Knowledge Base + AI search.
 *
 * Visibility: the public `listView` endpoint returns Public-only courses to
 * anonymous visitors and the broader catalogue once signed in. The query key
 * therefore includes `isAuthenticated` so it refetches when auth flips, and
 * `CourseCard` badges any non-Public course it shows. Finer role/right gating
 * is enforced by the backend on the course detail / play surfaces.
 */
export default function Home() {
  const publicCoursesService = usePublicCoursesService();
  const { isAuthenticated } = useOptionalAuth();

  // Enriched view — every row already has CategoryName / MinistryName /
  // VisibilityName so the cards don't need separate join calls. The
  // `isAuthenticated` key segment forces a refetch when the viewer signs in
  // or out, since the result set differs by auth state.
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['public', 'courses', 'view', 'home', isAuthenticated],
    queryFn: () => publicCoursesService.listView(),
  });

  // Featured = the catalogue's natural (admin-ordered) top slice.
  const featured = useMemo(() => courses.slice(0, FEATURED_LIMIT), [courses]);

  // Latest = newest by publish date, excluding anything already in Featured so
  // the two rails never repeat a course. Empty for small catalogues — the
  // section is then hidden rather than shown empty.
  const latest = useMemo(() => {
    const featuredIds = new Set(featured.map((c) => c.courseID));
    return [...courses]
      .sort((a, b) => publishedAt(b) - publishedAt(a))
      .filter((c) => !featuredIds.has(c.courseID))
      .slice(0, LATEST_LIMIT);
  }, [courses, featured]);

  // Ministries are derived straight from the (already visibility-filtered)
  // catalogue, so only ministries the viewer can actually see courses for are
  // offered — no separate query, and no dead links to empty ministries.
  const ministries = useMemo(() => {
    const map = new Map<number, { id: number; name: string; count: number }>();
    for (const c of courses) {
      if (c.ministryID == null || !c.ministryName) continue;
      const entry = map.get(c.ministryID) ?? { id: c.ministryID, name: c.ministryName, count: 0 };
      entry.count += 1;
      map.set(c.ministryID, entry);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [courses]);

  // Total published sessions across the visible catalogue, summed from the
  // enriched view's per-course `sessionCount`.
  const totalSessions = useMemo(
    () => courses.reduce((sum, c) => sum + (c.sessionCount ?? 0), 0),
    [courses],
  );

  // Hero stat strip — live catalogue counts plus one evergreen line. The
  // sessions entry only appears when the count resolves above zero, so the
  // strip never shows a misleading "0 sessions".
  const heroStats = [
    {
      icon: <IconBook size={15} />,
      label: `${courses.length} ${courses.length === 1 ? 'course' : 'courses'}`,
    },
    ...(totalSessions > 0
      ? [
          {
            icon: <IconPlayerPlay size={15} />,
            label: `${totalSessions} ${totalSessions === 1 ? 'session' : 'sessions'}`,
          },
        ]
      : []),
    { icon: <IconLock size={15} />, label: 'Free & member content' },
  ];

  return (
    <Stack gap={44}>
      {/* ─────────────── Hero ─────────────── */}
      <Box
        py={{ base: 20, sm: 24, md: 28 }}
        px={{ base: 'lg', sm: 'xl', md: 48 }}
        style={{
          background:
            'radial-gradient(circle at 90% 16%, var(--mantine-color-brand-4) 0%, transparent 46%), linear-gradient(115deg, var(--mantine-color-brand-8) 0%, var(--mantine-color-brand-6) 100%)',
          borderRadius: 'var(--mantine-radius-lg)',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        <Group align="center" justify="space-between" wrap="nowrap" gap="xl">
          {/* LEFT — copy, search and the stat strip. */}
          <Stack gap="sm" style={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
            {/* Pill badge — icon tile + label. */}
            <Group
              gap={8}
              wrap="nowrap"
              style={{
                alignSelf: 'flex-start',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: 999,
                padding: '4px 13px 4px 4px',
              }}
            >
              <Box
                style={{
                  background: 'white',
                  borderRadius: 6,
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconBook size={13} color="var(--mantine-color-brand-7)" />
              </Box>
              <Text fw={700} tt="uppercase" style={{ fontSize: 11, letterSpacing: '0.13em' }}>
                Acts Resource Centre
              </Text>
            </Group>

            <Title
              order={1}
              fw={800}
              style={{
                fontSize: 'clamp(1.6rem, 2.6vw + 0.8rem, 2.4rem)',
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
              }}
            >
              Everything you need to{' '}
              <Text component="span" inherit c="#0a3a26">
                serve well.
              </Text>
            </Title>

            <Text opacity={0.9} style={{ fontSize: 'clamp(0.9rem, 0.3vw + 0.82rem, 1rem)', lineHeight: 1.5 }}>
              Training videos, how-to guides and devotions from every ministry at Acts.
            </Text>

            <Box w="100%" maw={600}>
              {/* The full input + button row is too cramped on a phone, so
                  phones get the compact single-field search; sm+ gets the
                  full search bar. */}
              <Box hiddenFrom="sm">
                <AISearchBar compact />
              </Box>
              <Box visibleFrom="sm">
                <AISearchBar />
              </Box>
            </Box>

            {/* Stat strip — live counts where we have them, evergreen copy
                for the rest. Hidden until the catalogue has loaded. */}
            {courses.length > 0 && (
              <Group gap="lg" wrap="wrap" mt={4} style={{ opacity: 0.92 }}>
                {heroStats.map((s) => (
                  <Group key={s.label} gap={6} wrap="nowrap">
                    {s.icon}
                    <Text size="sm" fw={500}>
                      {s.label}
                    </Text>
                  </Group>
                ))}
              </Group>
            )}
          </Stack>

          {/* RIGHT — scripture quote card. Decorative, so it's hidden on
              narrower screens to keep the mobile banner compact. */}
          <Box visibleFrom="md" style={{ flexShrink: 0 }}>
            <Card radius="lg" p="xl" shadow="md" style={{ width: 264, background: 'white' }}>
              <IconQuote size={32} color="var(--mantine-color-brand-5)" />
              <Text
                fw={600}
                mt={6}
                style={{ fontSize: '1.2rem', lineHeight: 1.35, color: '#1f2d28' }}
              >
                Equip the saints for the work of ministry.
              </Text>
              <Text fw={600} size="sm" mt="sm" style={{ color: 'var(--mantine-color-brand-7)' }}>
                Ephesians 4:12
              </Text>
            </Card>
          </Box>
        </Group>
      </Box>

      {/* ─────────────── Featured courses ─────────────── */}
      <CourseSection
        title="Featured courses"
        description="Hand-picked across ministries — start anywhere."
        icon={<IconStar size={20} />}
        viewAllHref="/courses"
        viewAllLabel="Browse all"
        courses={featured}
        isLoading={isLoading}
        skeletonCount={6}
        emptyText="No public courses yet. Check back soon."
      />

      {/* ─────────────── Latest courses ─────────────── */}
      {(isLoading || latest.length > 0) && (
        <CourseSection
          title="Latest courses"
          description="Freshly published — the newest training added to the centre."
          icon={<IconClock size={20} />}
          viewAllHref="/courses"
          viewAllLabel="Browse all"
          courses={latest}
          isLoading={isLoading}
          skeletonCount={4}
        />
      )}

      {/* ─────────────── Browse by ministry ─────────────── */}
      {(isLoading || ministries.length > 0) && (
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap" gap="xs">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <ThemeIcon size={38} radius="md" variant="light" color="brand" visibleFrom="xs">
                <IconBuildingChurch size={20} />
              </ThemeIcon>
              <div style={{ minWidth: 0 }}>
                <Title order={2} style={{ fontSize: 'clamp(1.25rem, 2vw + 0.7rem, 1.6rem)' }}>
                  Browse by ministry
                </Title>
                <Text c="dimmed" size="sm">
                  Jump straight to the team you serve with.
                </Text>
              </div>
            </Group>
            <Button
              component={Link}
              to="/ministries"
              variant="subtle"
              size="sm"
              rightSection={<IconArrowRight size={16} />}
            >
              All ministries
            </Button>
          </Group>

          {isLoading ? (
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} height={68} radius="lg" />
              ))}
            </SimpleGrid>
          ) : (
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
              {ministries.slice(0, MINISTRY_LIMIT).map((m) => (
                <Card
                  key={m.id}
                  component={Link}
                  to={ministryHref({ name: m.name })}
                  withBorder
                  radius="lg"
                  padding="md"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size={32} radius="md" variant="light" color="brand">
                      <IconBuildingChurch size={16} />
                    </ThemeIcon>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" lineClamp={1}>
                        {m.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {m.count} course{m.count === 1 ? '' : 's'}
                      </Text>
                    </div>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Stack>
      )}

      {/* ─────────── Sign-in prompt — anonymous visitors only ─────────── */}
      {!isAuthenticated && (
        <Card withBorder radius="lg" p="lg" style={{ background: 'var(--mantine-color-brand-light)' }}>
          <Group justify="space-between" wrap="wrap" gap="md">
            <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 240 }}>
              <ThemeIcon size={46} radius="md" variant="light" color="brand">
                <IconLock size={22} />
              </ThemeIcon>
              <div>
                <Text fw={700}>More courses inside</Text>
                <Text size="sm" c="dimmed">
                  Some courses are reserved for members and ministry teams. Sign in to see
                  everything you have access to, track your progress and use the AI assistant.
                </Text>
              </div>
            </Group>
            <Button component={Link} to="/auth/login" size="md">
              Sign in
            </Button>
          </Group>
        </Card>
      )}

      {/* ─────────────── Knowledge Base + AI search ─────────────── */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        <Card withBorder padding="xl" radius="lg">
          <Group justify="space-between" mb="sm">
            <Title order={4}>Knowledge Base</Title>
            <ThemeIcon size={36} radius="md" variant="light" color="brand">
              <IconFileText size={20} />
            </ThemeIcon>
          </Group>
          <Text c="dimmed" mb="md">
            Practical how-to guides for setup teams and volunteers — from plugging in the sound desk
            to closing down on Sunday night. Sign in to access.
          </Text>
          <Button component={Link} to="/kb" variant="light">
            Open the Knowledge Base
          </Button>
        </Card>

        <Card withBorder padding="xl" radius="lg">
          <Group justify="space-between" mb="sm">
            <Title order={4}>Ask anything</Title>
            <ThemeIcon size={36} radius="md" variant="light" color="brand">
              <IconSparkles size={20} />
            </ThemeIcon>
          </Group>
          <Text c="dimmed" mb="md">
            Plain-English search across every article and course. The assistant cites the exact
            steps so you can trust the answer.
          </Text>
          <Button component={Link} to="/search" variant="light">
            Try the AI search
          </Button>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
